import { useEffect, useLayoutEffect } from 'react';

import { MdError } from 'react-icons/md';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';

import InsightsHubLayout from './components/InsightsHubLayout';
import MessagePage from './components/MessagePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProjectScreenerGuard } from './pages/projectScreener/ProjectScreenerGuard';
import { compassProjectTypeChildren } from './routes/children/compassRoutes';
import { projectDetailChildren } from './routes/children/projectRoutes';
import {
  AccountRequest,
  Compass,
  Contact,
  DeveloperPortal,
  ExpertAdvisory,
  ExpertAdvisoryLearnMore,
  FreeDashboard,
  Home,
  InsightsHub,
  InsightsHubArticle,
  InsightsHubCategory,
  InsightsHubPinnedPost,
  InsightsHubResearchUpdate,
  LazyRoute,
  NewScreening,
  Programs,
  Project,
  ProjectScreener,
  ProjectType,
  Register,
  RetireeInsights,
  RetireeInsightsDrillDown,
  ScreenerFAQ,
  ScreeningResults,
  Search,
  SignUp,
  SupplyLink,
  Upgrade,
  WhereToBuy,
} from './routes/lazyImports';
import { page } from './services/analytics';
import { withFaroRouterInstrumentation } from './services/faro/faroRouter';
import Sentry from './services/sentry';

let previousPath = '/';
const NavigateToProfileSettings = () => {
  useLayoutEffect(() => {
    window.location.href = process.env.IS_PRODUCTION_APP
      ? 'https://auth.calyxglobal.com/account'
      : 'https://auth.calyxstaging.com/account';
  }, []);
  return <></>;
};

const AppLayout = () => {
  const location = useLocation();

  useEffect(() => {
    page();
    if (!(location.state as { preventScrollReset?: boolean })?.preventScrollReset) {
      const layout = document.getElementById('layout');
      layout?.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <MessagePage
          title="Oops, that's our bad!"
          description="We're not exactly sure what happened, rest assured we've been notified about this issue and this will be fixed soon!"
          Icon={MdError}
          hasAction
          resetError={resetError}
        />
      )}
    >
      <ScrollRestoration />
      <Outlet />
    </Sentry.ErrorBoundary>
  );
};

const loaderCallback = ({ request }) => {
  const prev = previousPath;
  previousPath = new URL(request.url).pathname;
  return { prevPath: prev };
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: (
      <MessagePage
        title='Page not found'
        description='The page you are looking for doesn’t exist or has been moved.'
        hasAction
      />
    ),
    children: [
      {
        path: '/',
        element: <ProtectedRoute />,
        children: [
          {
            path: `/`,
            element: <LazyRoute Component={Home} />,
          },
          {
            path: '/login',
            element: <Navigate to='/' />,
          },
          {
            path: '/developer-portal',
            element: <LazyRoute Component={DeveloperPortal} />,
          },
          {
            path: 'research-hub',
            element: <InsightsHubLayout />,
            children: [
              {
                path: '',
                element: <LazyRoute Component={InsightsHub} />,
              },
              {
                path: 'pinned/:slug',
                element: <LazyRoute Component={InsightsHubPinnedPost} />,
              },
              {
                path: ':category',
                element: <LazyRoute Component={InsightsHubCategory} />,
              },
              {
                path: ':category/:slug',
                element: <LazyRoute Component={InsightsHubArticle} />,
              },
              {
                path: 'research_update/:slug',
                element: <LazyRoute Component={InsightsHubResearchUpdate} />,
              },
            ],
          },
          // redirect old blog/resources/notes to research-hub start
          {
            path: 'blog_detail_dist',
            element: (
              <Navigate
                to={`/research-hub/pinned/current-calyx-risk-ratings-distribution${window.location.search}`}
                replace
              />
            ),
          },
          {
            path: 'blog',
            element: <Navigate to={`/research-hub${window.location.search}`} replace />,
          },
          {
            path: 'resource',
            element: <Navigate to={`/research-hub${window.location.search}`} replace />,
          },
          {
            path: 'note',
            element: <Navigate to={`/research-hub${window.location.search}`} replace />,
          },
          // redirect old blog/resources/notes to research-hub end
          {
            path: `/credit-ratings`,
            element: <LazyRoute Component={Search} />,
            loader: loaderCallback,
          },
          // redirect to new search page
          {
            path: `/search`,
            element: <Navigate to={`/credit-ratings`} replace />,
            loader: loaderCallback,
          },
          {
            path: `/supply-link`,
            element: <Navigate to={`/supply-bridge`} replace />,
          },
          // redirect to supply bridge
          {
            path: `/supply-bridge`,
            element: <LazyRoute Component={SupplyLink} />,
          },
          {
            path: `/compass`,
            element: <LazyRoute Component={Compass} />,
          },
          {
            path: '/compass/:projectType',
            element: <LazyRoute Component={ProjectType} />,
            children: compassProjectTypeChildren,
          },
          {
            path: `/expert-advisory`,
            element: <LazyRoute Component={ExpertAdvisory} />,
          },
          {
            path: `/expert-advisory/:service_name`,
            element: <LazyRoute Component={ExpertAdvisoryLearnMore} />,
          },
          {
            path: '/project-screener',
            element: <ProjectScreenerGuard />,
            children: [
              {
                index: true,
                element: <LazyRoute Component={ProjectScreener} />,
              },
              {
                path: 'new',
                element: <LazyRoute Component={NewScreening} />,
              },
              {
                path: 'new/faq',
                element: <LazyRoute Component={ScreenerFAQ} />,
              },
              {
                path: ':id',
                element: <LazyRoute Component={ScreeningResults} />,
              },
            ],
          },
          {
            path: `/`,
            element: <LazyRoute Component={Project} />,
            children: projectDetailChildren(loaderCallback),
          },
          {
            path: `/where_to_buy`,
            element: <LazyRoute Component={WhereToBuy} />,
          },
          {
            path: 'retirement_insights',
            element: <LazyRoute Component={RetireeInsights} />,
          },
          {
            path: 'retirement_insights/:retiree',
            element: <LazyRoute Component={RetireeInsightsDrillDown} />,
          },
          {
            path: 'contact',
            element: <LazyRoute Component={Contact} />,
          },
          {
            path: 'profile',
            element: <NavigateToProfileSettings />,
          },
          {
            path: 'dashboard',
            element: <Navigate to='/' />,
          },
          {
            path: '/programs',
            element: <LazyRoute Component={Programs} />,
          },
          {
            path: '/upgrade',
            element: <LazyRoute Component={Upgrade} />,
          },
        ],
      },
      {
        path: '/signup',
        element: <LazyRoute Component={SignUp} />, // account signup form for secondnature universities
      },
      {
        path: '/account-signup',
        element: <LazyRoute Component={SignUp} signupType='b2b2b' />, // generic account sign up form for all users coming from B2B2B
      },
      {
        path: '/account-request',
        element: <LazyRoute Component={AccountRequest} />,
      },
      {
        path: '/register',
        element: <LazyRoute Component={Register} />,
      },
      {
        path: '/free-dashboard',
        element: <LazyRoute Component={FreeDashboard} />,
      },
    ],
  },
]);

// Initialize Faro with router instrumentation
const instrumentedRouter = withFaroRouterInstrumentation(router);

export const Routes = () => {
  return <RouterProvider router={instrumentedRouter} />;
};
